import { Controller } from "@hotwired/stimulus"

// The opening countdown (docs/07 "Countdown on server time", T51a).
//
// The device's clock is not the authority. A phone five minutes fast would
// show Start five minutes early and every one of those submissions would be
// refused; five minutes slow and the person watches a countdown while the
// booths go. So the controller asks the server what time it is, works out the
// OFFSET from its own clock once, and counts with that.
//
// Three consequences, each of them the point:
//
//   **A stale page still opens on time.** An hour-old copy from a proxy has
//   an hour-old `opens_at` baked into it, but the first `/time` reply
//   replaces it. Nothing here trusts what the page was rendered with.
//
//   **It re-asks.** Once a minute, and whenever the tab comes back — a phone
//   that slept through the last ten minutes has a clock that drifted and a
//   countdown that stopped.
//
//   **At zero it enables the button and stops.** No reload, no random delay
//   (docs/07). A page that reloads itself at the opening moment is two hundred
//   people all reloading at the same second, and a random delay is a lottery
//   dressed up as fairness.
export default class extends Controller {
  static targets = ["remaining", "start", "waiting", "open"]
  static values = {
    url: String,
    opensAt: String,
    // Seconds. The server's own answer at render time, used until the first
    // `/time` reply lands so the first paint is never blank.
    remaining: Number
  }

  static RESYNC_MS = 60000

  connect() {
    this.offset = 0
    this.opensAt = this.opensAtValue ? Date.parse(this.opensAtValue) : null

    this.onVisible = () => { if (!document.hidden) this.sync() }
    document.addEventListener("visibilitychange", this.onVisible)

    this.tick()
    this.ticker = setInterval(() => this.tick(), 1000)
    this.resyncer = setInterval(() => this.sync(), this.constructor.RESYNC_MS)
    this.sync()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisible)
    clearInterval(this.ticker)
    clearInterval(this.resyncer)
  }

  // One request, one offset. The round trip is not halved or corrected for:
  // being a few hundred milliseconds out does not matter, and pretending to
  // that precision would only hide the fact that the answer is a second old.
  async sync() {
    if (!this.urlValue) return

    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) return

      const { now, opens_at: opensAt } = await response.json()

      this.offset = Date.parse(now) - Date.now()
      // The server's opening time wins over the page's: a moved opening has to
      // reach a page that is already open (docs/07).
      if (opensAt) this.opensAt = Date.parse(opensAt)

      this.tick()
    } catch {
      // Offline. The countdown keeps running on the last offset it had, which
      // is better than freezing, and the next sync corrects it.
    }
  }

  get secondsLeft() {
    if (this.opensAt === null) return this.remainingValue

    // CEIL, not round: at 400 ms remaining, rounding gives zero and the button
    // enables before the server would accept anything — which is the exact
    // failure this whole mechanism exists to prevent.
    return Math.max(0, Math.ceil((this.opensAt - (Date.now() + this.offset)) / 1000))
  }

  tick() {
    const left = this.secondsLeft

    if (this.hasRemainingTarget) this.remainingTarget.textContent = this.format(left)

    if (left > 0) return

    this.open()
  }

  open() {
    clearInterval(this.ticker)

    this.startTargets.forEach((target) => { target.disabled = false })
    this.waitingTargets.forEach((target) => { target.hidden = true })
    this.openTargets.forEach((target) => { target.hidden = false })
  }

  // "2 days, 04:11:09" — the days said in words because "2:04:11:09" is a
  // number nobody parses at a glance.
  format(seconds) {
    const days = Math.floor(seconds / 86400)
    const rest = seconds % 86400
    const clock = [ Math.floor(rest / 3600), Math.floor((rest % 3600) / 60), rest % 60 ]
      .map((part) => String(part).padStart(2, "0"))
      .join(":")

    if (days === 0) return clock

    return `${days} ${days === 1 ? "day" : "days"}, ${clock}`
  }
}
